import Box from '@mui/material/Box';
import FolderIcon from '@mui/icons-material/Folder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityIcon from '@mui/icons-material/Security';
import { getSecurityNavItemPath } from 'src/mindtime/config/securityNav';
import type { WazuhFimEventItem, WazuhFimRule } from 'src/types/mindtime/wazuhFileMonitoring';
import {
  EDR_COLORS,
  EdrDateTime,
  EdrDetailEndpointSidebar,
  EdrDetailKeyValueRows,
  EdrDetailSectionCard,
  EdrExpandableText,
  EdrSeverityPill,
  EdrStatusPill,
  formatEdrDateTime,
  type EdrEntityDetailPageProps,
  type EdrSeverityLevel,
  type EdrStatusTone,
} from '../shared/edrPrimitives';

export const CHANGE_TYPE_COLORS: Record<string, string> = {
  modified: EDR_COLORS.medium,
  deleted: EDR_COLORS.critical,
  added: EDR_COLORS.brand500,
  renamed: EDR_COLORS.slate500,
};

const normalizeKey = (value: string | undefined | null): string =>
  (value ?? '').toString().trim().toLowerCase();

export const toFimTitle = (value: string | undefined | null): string => {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const fimSeverityToLevel = (sev: string | undefined): EdrSeverityLevel => {
  const key = normalizeKey(sev);
  if (key === 'critical' || key === 'high' || key === 'medium' || key === 'low') return key;
  return 'medium';
};

export const fimStatusToTone = (status: string | undefined): EdrStatusTone => {
  const key = normalizeKey(status);
  if (key === 'open') return 'critical';
  if (key === 'investigating') return 'medium';
  if (key === 'resolved') return 'success';
  return 'neutral';
};

export const fimChangeTypeToTone = (changeType: string | undefined): EdrStatusTone => {
  const key = normalizeKey(changeType);
  if (key === 'deleted') return 'critical';
  if (key === 'modified') return 'medium';
  if (key === 'added' || key === 'created') return 'brand';
  return 'neutral';
};

export const formatFimRuleDetail = (rule: WazuhFimRule | undefined): string => {
  if (!rule) return '—';
  const description = rule.description?.trim();
  const id = rule.id != null && String(rule.id).trim() ? String(rule.id).trim() : '';
  const level = typeof rule.level === 'number' ? `L${rule.level}` : '';
  const suffixParts = [id ? `#${id}` : '', level].filter(Boolean);
  const suffix = suffixParts.length ? ` ${suffixParts.join(' - ')}` : '';
  if (description) return `${description}${suffix}`.trim();
  return suffix.trim() || '—';
};

export const formatFimFileName = (filePath: string | undefined): string =>
  filePath?.split(/[\\/]/).pop() || filePath || '—';

/** Id for GET /file-monitoring/events/{id} — prefers `id`, then `_id`. */
export const getFimEventId = (
  item: Pick<WazuhFimEventItem, 'id' | '_id'> | null | undefined
): string => String(item?.id ?? item?._id ?? '').trim();

export const normalizeFimKey = normalizeKey;

export const buildFimDetailProps = (
  item: WazuhFimEventItem
): Omit<EdrEntityDetailPageProps, 'loading' | 'notFoundMessage'> => {
  const severityLevel = fimSeverityToLevel(item.severity);
  const changeLabel = toFimTitle(item.changeType) || 'Change';
  const statusLabel = toFimTitle(item.status) || changeLabel;
  const statusTone = item.status
    ? fimStatusToTone(item.status)
    : fimChangeTypeToTone(item.changeType);
  const fileName = formatFimFileName(item.filePath);

  return {
    backLink: {
      label: 'Back to File Monitoring',
      to: getSecurityNavItemPath('file-integrity-monitoring') ?? '',
    },
    title: fileName,
    badges: (
      <>
        <EdrSeverityPill level={severityLevel} label={toFimTitle(item.severity) || '—'} />
        <EdrStatusPill tone={fimChangeTypeToTone(item.changeType)} label={changeLabel} />
      </>
    ),
    metaLine: (
      <>
        {changeLabel}
        {' · '}
        {formatEdrDateTime(item.timestamp)}
        {' on '}
        <strong style={{ color: EDR_COLORS.textPrimary }}>{item.endpoint || '—'}</strong>
      </>
    ),
    main: (
      <>
        <EdrDetailSectionCard icon={<FolderIcon style={{ fontSize: 16 }} />} title="File Event">
          <EdrDetailKeyValueRows
            rows={[
              { label: 'File Name', value: fileName },
              { label: 'Full Path', value: item.filePath || '—', mono: true },
              { label: 'Change Type', value: changeLabel },
              { label: 'User', value: item.user || '—', mono: true },
              { label: 'Timestamp', value: <EdrDateTime value={item.timestamp} /> },
            ]}
          />
        </EdrDetailSectionCard>

        <EdrDetailSectionCard icon={<SecurityIcon style={{ fontSize: 16 }} />} title="Rule">
          {item.rule?.description ? (
            <Box mb={1.5}>
              <EdrExpandableText text={item.rule.description} lines={3} />
            </Box>
          ) : null}
          <EdrDetailKeyValueRows
            rows={[
              {
                label: 'Level',
                value: typeof item.rule?.level === 'number' ? String(item.rule.level) : '—',
              },
              {
                label: 'Rule ID',
                value: item.rule?.id != null ? String(item.rule.id) : '—',
                mono: true,
              },
              {
                label: 'Groups',
                value: item.rule?.groups?.length ? item.rule.groups.join(', ') : '—',
              },
            ]}
          />
        </EdrDetailSectionCard>
      </>
    ),
    sidebar: (
      <>
        <EdrDetailEndpointSidebar
          endpoint={item.endpoint || '—'}
          statusLabel={statusLabel}
          statusTone={statusTone}
          organization={item.organization}
        />
        <EdrDetailSectionCard
          icon={<InfoOutlinedIcon style={{ fontSize: 16 }} />}
          title="Event Details"
        >
          <EdrDetailKeyValueRows
            rows={[
              { label: 'Severity', value: toFimTitle(item.severity) || '—' },
              { label: 'Status', value: toFimTitle(item.status) || '—' },
              { label: 'Wazuh Agent', value: item.wazuhAgentId || '—', mono: true },
              { label: 'Wazuh Alert', value: item.wazuhAlertId || '—', mono: true },
            ]}
          />
        </EdrDetailSectionCard>
      </>
    ),
  };
};
